import BaseStockProvider from '../BaseStockProvider';

/**
 * Openverse (Creative Commons) stock media provider
 * API Documentation: https://api.openverse.org/v1/
 *
 * Openverse provides access to over 700 million Creative Commons licensed
 * images from various sources.
 *
 * Note: Audio support has been disabled for this provider.
 *
 * License Types:
 * - CC0 (Public Domain)
 * - CC BY (Attribution)
 * - CC BY-SA (Attribution-ShareAlike)
 * - CC BY-NC (Attribution-NonCommercial)
 * - CC BY-ND (Attribution-NoDerivs)
 * - CC BY-NC-SA (Attribution-NonCommercial-ShareAlike)
 * - CC BY-NC-ND (Attribution-NonCommercial-NoDerivs)
 * - PDM (Public Domain Mark)
 */
class OpenverseProvider extends BaseStockProvider {
  constructor(apiKey = null) {
    super({
      name: 'Openverse',
      apiKey: apiKey || process.env.REACT_APP_OPENVERSE_API_KEY,
      baseUrl: 'https://api.openverse.org/v1',
      mediaTypes: ['image'], // Audio support disabled
      requiresAuth: false, // API works without auth but with rate limits
    });
    
    this.clientId = process.env.REACT_APP_OPENVERSE_CLIENT_ID;
    this.clientSecret = process.env.REACT_APP_OPENVERSE_CLIENT_SECRET;
    this.accessToken = null;
    this.tokenExpiry = null;
  }

  /**
   * Get OAuth access token for higher rate limits
   * Without auth: 100 requests/day per IP
   * With auth: 10,000 requests/day
   */
  async getAccessToken() {
    // If we have a valid token, return it
    if (this.accessToken && this.tokenExpiry && Date.now() < this.tokenExpiry) {
      return this.accessToken;
    }

    // If no credentials, work without auth (lower rate limits)
    if (!this.clientId || !this.clientSecret) {
      return null;
    }

    try {
      const response = await fetch(`${this.baseUrl}/auth_tokens/token/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'client_credentials',
          client_id: this.clientId,
          client_secret: this.clientSecret,
        }),
      });

      if (!response.ok) {
        console.warn('Failed to get Openverse access token, using unauthenticated mode');
        return null;
      }

      const data = await response.json();
      this.accessToken = data.access_token;
      // Token expires in 12 hours, refresh 1 hour before
      this.tokenExpiry = Date.now() + (11 * 60 * 60 * 1000);
      
      return this.accessToken;
    } catch (error) {
      console.error('Error getting Openverse access token:', error);
      return null;
    }
  }

  /**
   * Build request headers
   */
  async getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    const token = await this.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Search for media on Openverse
   */
  async search({ query, mediaType = 'image', page = 1, perPage = 20 }) {
    try {
      const endpoint = mediaType === 'audio' ? '/audio' : '/images';
      const headers = await this.getHeaders();
      
      const params = new URLSearchParams({
        q: query,
        page: page.toString(),
        page_size: perPage.toString(),
        mature: 'false',
      });

      const url = `${this.baseUrl}${endpoint}/?${params}`;
      
      const response = await fetch(url, { headers });

      if (!response.ok) {
        if (response.status === 429) {
          console.warn('Openverse rate limit exceeded');
        }
        throw new Error(`Openverse API error: ${response.status}`);
      }

      const data = await response.json();
      const items = data.results || [];
      
      // Normalize and filter out invalid formats
      const normalizedItems = items.map(item => this.normalizeItem(item, mediaType));
      const filteredItems = this.filterValidFormats(normalizedItems);
      
      return {
        data: filteredItems,
        hasMore: data.page_count > page,
        total: data.result_count,
        page: page,
      };
    } catch (error) {
      console.error('Openverse search error:', error);
      return { data: [], hasMore: false, total: 0 };
    }
  }

  /**
   * Get popular/trending media
   * Openverse doesn't have a dedicated trending endpoint, so we search for popular topics
   */
  async getPopular({ mediaType = 'image', page = 1, perPage = 20 }) {
    try {
      const endpoint = mediaType === 'audio' ? '/audio' : '/images';
      const headers = await this.getHeaders();
      
      // Use empty query to get general results, sorted by relevance
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: perPage.toString(),
        mature: 'false',
      });

      const url = `${this.baseUrl}${endpoint}/?${params}`;
      
      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error(`Openverse API error: ${response.status}`);
      }

      const data = await response.json();
      const items = data.results || [];
      
      // Normalize and filter out invalid formats
      const normalizedItems = items.map(item => this.normalizeItem(item, mediaType));
      const filteredItems = this.filterValidFormats(normalizedItems);
      
      return {
        data: filteredItems,
        hasMore: data.page_count > page,
        total: data.result_count,
        page: page,
      };
    } catch (error) {
      console.error('Openverse getPopular error:', error);
      return { data: [], hasMore: false, total: 0 };
    }
  }

  /**
   * Normalize Openverse item to common format
   */
  normalizeItem(item, mediaType) {
    if (mediaType === 'audio') {
      return this.normalizeAudio(item);
    }
    return this.normalizeImage(item);
  }

  /**
   * Format license for display
   */
  formatLicense(license, licenseVersion) {
    const licenseMap = {
      'cc0': 'CC0 (Public Domain)',
      'pdm': 'Public Domain Mark',
      'by': 'CC BY',
      'by-sa': 'CC BY-SA',
      'by-nc': 'CC BY-NC',
      'by-nd': 'CC BY-ND',
      'by-nc-sa': 'CC BY-NC-SA',
      'by-nc-nd': 'CC BY-NC-ND',
    };

    const licenseName = licenseMap[license?.toLowerCase()] || license?.toUpperCase() || 'Unknown';
    return licenseVersion ? `${licenseName} ${licenseVersion}` : licenseName;
  }

  /**
   * Build attribution text according to CC best practices
   */
  buildAttribution(item, mediaType = 'image') {
    const type = mediaType === 'audio' ? 'Audio' : 'Image';
    const title = item.title || 'Untitled';
    const creator = item.creator || 'Unknown';
    const license = this.formatLicense(item.license, item.license_version);
    const source = item.source || 'Openverse';

    // Format: "Title" by Creator is licensed under License via Source
    if (item.license?.toLowerCase() === 'cc0' || item.license?.toLowerCase() === 'pdm') {
      return `"${title}" by ${creator} (${license}) via ${source}`;
    }
    
    return `"${title}" by ${creator} is licensed under ${license} via ${source}`;
  }

  /**
   * Normalize Openverse image
   */
  normalizeImage(item) {
    return {
      id: `openverse_image_${item.id}`,
      provider: 'openverse',
      name: item.title || 'Untitled',
      thumbnail: item.thumbnail || item.url,
      url: item.url,
      downloadUrl: item.url,
      type: 'image',
      category: 'Creative Commons',
      description: item.title || item.tags?.map(t => t.name).join(', ') || '',
      width: item.width,
      height: item.height,
      author: {
        name: item.creator || 'Unknown',
        username: item.creator || 'unknown',
        profileUrl: item.creator_url || item.foreign_landing_url,
      },
      sourceUrl: item.foreign_landing_url,
      attribution: this.buildAttribution(item, 'image'),
      attributionUrl: item.foreign_landing_url,
      attributionHtml: item.attribution, // Openverse provides HTML attribution
      isUserUpload: false,
      // CC-specific fields
      license: item.license,
      licenseVersion: item.license_version,
      licenseUrl: item.license_url,
      source: item.source,
      tags: item.tags?.map(t => t.name) || [],
    };
  }

  /**
   * Normalize Openverse audio
   */
  normalizeAudio(item) {
    // Format duration from seconds to MM:SS
    const formatDuration = (seconds) => {
      if (!seconds) return null;
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return {
      id: `openverse_audio_${item.id}`,
      provider: 'openverse',
      name: item.title || 'Untitled',
      thumbnail: item.thumbnail || null,
      url: item.url,
      downloadUrl: item.url,
      type: 'audio',
      category: 'Creative Commons',
      description: item.title || item.tags?.map(t => t.name).join(', ') || '',
      duration: formatDuration(item.duration),
      durationSeconds: item.duration,
      author: {
        name: item.creator || 'Unknown',
        username: item.creator || 'unknown',
        profileUrl: item.creator_url || item.foreign_landing_url,
      },
      sourceUrl: item.foreign_landing_url,
      attribution: this.buildAttribution(item, 'audio'),
      attributionUrl: item.foreign_landing_url,
      attributionHtml: item.attribution,
      isUserUpload: false,
      // CC-specific fields
      license: item.license,
      licenseVersion: item.license_version,
      licenseUrl: item.license_url,
      source: item.source,
      tags: item.tags?.map(t => t.name) || [],
      // Audio-specific fields
      genres: item.genres || [],
      audioSet: item.audio_set,
      sampleRate: item.sample_rate,
      bitRate: item.bit_rate,
      filesize: item.filesize,
    };
  }

  /**
   * Get attribution text for display
   */
  getAttributionText(item, mediaType = 'image') {
    return item.attribution || this.buildAttribution(item, mediaType);
  }

  /**
   * Get attribution HTML (Openverse provides this)
   */
  getAttributionHtml(item) {
    return item.attributionHtml || null;
  }
}

export default OpenverseProvider;
